import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { relativeTime } from "../lib/format";

// Channels + statuses mirror convex/schema.ts (channelName / releaseStatus).
const CHANNELS = ["stable", "beta", "exp-A", "exp-B"] as const;
type Channel = (typeof CHANNELS)[number];

// updateJob lifecycle states (convex/schema.ts updateJobState). The rollout
// monitor renders a per-device chip for each. Order = rough progression.
const JOB_STATES = [
  "pending",
  "downloading",
  "verifying",
  "applying",
  "healthcheck",
  "done",
  "rolledback",
  "failed",
] as const;

// Screen 2 — Releases, channels & rollout monitor (Design Doc 2.7 #2).
//
// Phase 3 backend is live:
// - createRelease (config-only OR signed app-update artifact)
// - startRollout (PUBLIC, auth-gated) kicks the durable canary->fleet workflow
// - rolloutStatus (reactive) feeds the ROLLOUT MONITOR: canary selftest health
//   + per-device updateJob progress
// - killRelease (auth-gated) = emergency brake; reverts in-flight devices
//
// 401 from startRollout/killRelease is surfaced inline (operator must be signed
// in). The kill switch uses an INLINE confirm (no window.confirm — a native
// modal blocks browser automation).
export function Releases() {
  const releases = useQuery(api.releases.listReleases, {});
  const [monitorId, setMonitorId] = useState<Id<"releases"> | null>(null);

  const promoteChannel = useMutation(api.releases.promoteChannel);
  const rollbackChannel = useMutation(api.releases.rollbackChannel);

  return (
    <div className="screen">
      <header className="screen-head">
        <h1>Releases &amp; channels</h1>
        <p className="screen-sub">
          A release is config (+ optional signed artifact). Most iteration is a
          pure config push — instant, no download. App updates ship a verified
          artifact (SHA-256 + Ed25519, checked on-device).
        </p>
      </header>

      <div className="cols">
        <div className="col">
          <CreateReleaseForm />
        </div>
        <div className="col">
          <StartRolloutForm releases={releases} />
        </div>
      </div>

      <section className="panel">
        <h2 className="panel-title">Releases</h2>
        {releases === undefined ? (
          <Skeleton />
        ) : releases.length === 0 ? (
          <EmptyState
            title="No releases yet"
            body="Create one on the left. It starts as a draft."
          />
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Version</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Type</th>
                <th>Created</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {releases.map((r) => (
                <tr key={r._id}>
                  <td className="strong">{r.version}</td>
                  <td>
                    <span className="chip">{r.channel}</span>
                  </td>
                  <td>
                    <span className={`tag status-tag-${r.status}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="muted">
                    {r.requiresAppDownload ? "app update" : "config-only"}
                  </td>
                  <td className="muted">{relativeTime(r.createdAt)}</td>
                  <td className="muted truncate">{r.notes ?? "—"}</td>
                  <td className="actions">
                    <button
                      className="btn btn-sm"
                      title="Open rollout monitor"
                      onClick={() =>
                        setMonitorId((cur) => (cur === r._id ? null : r._id))
                      }
                    >
                      {monitorId === r._id ? "Hide monitor" : "Monitor"}
                    </button>
                    <button
                      className="btn btn-sm"
                      title="Mark active"
                      disabled={r.status === "active"}
                      onClick={() => void promoteChannel({ releaseId: r._id })}
                    >
                      Promote
                    </button>
                    <button
                      className="btn btn-sm"
                      title="Mark rolled back"
                      disabled={r.status === "rolledback"}
                      onClick={() => void rollbackChannel({ releaseId: r._id })}
                    >
                      Rollback
                    </button>
                    <KillSwitch
                      releaseId={r._id}
                      version={r.version}
                      disabled={r.status === "killed"}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {monitorId && (
        <RolloutMonitor
          releaseId={monitorId}
          onClose={() => setMonitorId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ROLLOUT MONITOR — reactive read of rolloutStatus(releaseId).
// Canary device + its selftest health, then fleet progress (per-device job
// states tallied + listed). Subscribes live; updates as the workflow advances.
// ---------------------------------------------------------------------------
function RolloutMonitor({
  releaseId,
  onClose,
}: {
  releaseId: Id<"releases">;
  onClose: () => void;
}) {
  const status = useQuery(api.rollout.rolloutStatus, { releaseId });

  return (
    <section className="panel monitor">
      <div className="monitor-head">
        <h2 className="panel-title">Rollout monitor</h2>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>
          Close
        </button>
      </div>

      {status === undefined ? (
        <Skeleton />
      ) : status === null ? (
        <EmptyState
          title="Release not found"
          body="It may have been removed."
        />
      ) : (
        <div className="monitor-body">
          <div className="monitor-summary">
            <span className="strong">{status.version}</span>
            <span className="chip">{status.channel}</span>
            <span className={`tag status-tag-${status.status}`}>
              {status.status}
            </span>
            <span className="muted">
              {status.jobs.length} device job(s)
            </span>
          </div>

          {/* Canary gate */}
          <h3 className="section-title">Canary gate</h3>
          {status.canary === null ? (
            <div className="row-empty">
              No canary yet — start the rollout to assign one.
            </div>
          ) : (
            <div className="canary-card">
              <div className="canary-id">
                <span className="muted">Canary device</span>
                <span className="strong">
                  {status.canary.deviceName ?? status.canary.deviceId}
                </span>
              </div>
              <div
                className={`canary-health ${
                  status.canary.healthy ? "ok" : "wait"
                }`}
              >
                <span className="dot" />
                {status.canary.healthy
                  ? "selftest passed"
                  : "awaiting selftest"}
              </div>
            </div>
          )}

          {/* Fleet progress */}
          <h3 className="section-title">Fleet progress</h3>
          {status.jobs.length === 0 ? (
            <div className="row-empty">No update jobs opened yet.</div>
          ) : (
            <>
              <div className="state-tally">
                {JOB_STATES.map((s) => {
                  const n = status.jobsByState[s] ?? 0;
                  if (n === 0) return null;
                  return (
                    <span key={s} className={`job-pill job-${s}`}>
                      {s} · {n}
                    </span>
                  );
                })}
              </div>
              <table className="grid">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>State</th>
                    <th>Started</th>
                    <th>Finished</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {status.jobs.map((j, i) => (
                    <tr key={j._id}>
                      <td className="strong">
                        {j.deviceName ?? j.deviceId}
                        {i === 0 && (
                          <span className="canary-flag">canary</span>
                        )}
                      </td>
                      <td>
                        <span className={`job-pill job-${j.state}`}>
                          {j.state}
                        </span>
                      </td>
                      <td className="muted">{relativeTime(j.startedAt)}</td>
                      <td className="muted">
                        {j.finishedAt ? relativeTime(j.finishedAt) : "—"}
                      </td>
                      <td className="muted truncate">{j.error ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// KILL SWITCH — inline two-step confirm (NO window.confirm; a native modal
// blocks browser automation). Auth-gated mutation; 401 surfaced inline.
// ---------------------------------------------------------------------------
function KillSwitch({
  releaseId,
  version,
  disabled,
}: {
  releaseId: Id<"releases">;
  version: string;
  disabled: boolean;
}) {
  const killRelease = useMutation(api.releases.killRelease);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function doKill() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await killRelease({ releaseId });
      setMsg(`Killed — ${res.reverted} device(s) reverted.`);
      setConfirming(false);
    } catch (err) {
      setMsg(authMessage(err, "kill release"));
    } finally {
      setBusy(false);
    }
  }

  if (disabled) {
    return (
      <button className="btn btn-sm btn-danger" disabled title="Already killed">
        Kill
      </button>
    );
  }

  if (confirming) {
    return (
      <span className="kill-confirm">
        <span className="kill-confirm-q">Kill {version}?</span>
        <button
          className="btn btn-sm btn-danger"
          disabled={busy}
          onClick={() => void doKill()}
        >
          {busy ? "Killing…" : "Confirm kill"}
        </button>
        <button
          className="btn btn-sm btn-ghost"
          disabled={busy}
          onClick={() => setConfirming(false)}
        >
          Cancel
        </button>
        {msg && <span className="kill-msg">{msg}</span>}
      </span>
    );
  }

  return (
    <span className="kill-wrap">
      <button
        className="btn btn-sm btn-danger"
        title="Global kill switch — reverts in-flight devices to last-known-good"
        onClick={() => {
          setMsg(null);
          setConfirming(true);
        }}
      >
        Kill
      </button>
      {msg && <span className="kill-msg">{msg}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// CREATE RELEASE — config-only OR an app-update with signed artifact refs.
// ---------------------------------------------------------------------------
const DEFAULT_CONFIG = `{
  "vlm_model": "qwen2.5vl:7b",
  "sensitivity_threshold": 0.6,
  "high_confidence_score": 0.9,
  "blur_radius": 25,
  "capture_interval_s": 4
}`;

function CreateReleaseForm() {
  const createRelease = useMutation(api.releases.createRelease);
  const [version, setVersion] = useState("");
  const [channel, setChannel] = useState<Channel>("exp-A");
  const [configText, setConfigText] = useState(DEFAULT_CONFIG);
  const [isAppUpdate, setIsAppUpdate] = useState(false);
  const [artifactKey, setArtifactKey] = useState("");
  const [artifactSha256, setArtifactSha256] = useState("");
  const [artifactSig, setArtifactSig] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(false);

    let configJson: unknown;
    try {
      configJson = JSON.parse(configText);
    } catch {
      setError("configJson is not valid JSON.");
      return;
    }

    if (isAppUpdate && !artifactKey.trim()) {
      setError("App update needs an artifact object key.");
      return;
    }

    setBusy(true);
    try {
      // configJson is the schema's releaseConfig object (all keys optional).
      // Artifact refs only sent for an app update; the device verifies
      // SHA-256 + Ed25519 before applying, regardless of what we send.
      await createRelease({
        version: version.trim(),
        channel,
        configJson: configJson as Record<string, unknown>,
        requiresAppDownload: isAppUpdate,
        ...(isAppUpdate
          ? {
              appArtifactKey: artifactKey.trim(),
              appArtifactSha256: artifactSha256.trim() || undefined,
              appArtifactSig: artifactSig.trim() || undefined,
            }
          : {}),
        notes: notes.trim() || undefined,
      });
      setVersion("");
      setNotes("");
      setArtifactKey("");
      setArtifactSha256("");
      setArtifactSig("");
      setOk(true);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={submit}>
      <h2 className="panel-title">Create release</h2>
      <label className="field">
        <span>Version</span>
        <input
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="2026.06.15-a"
          required
        />
      </label>
      <label className="field">
        <span>Channel</span>
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as Channel)}
        >
          {CHANNELS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>configJson (pushed live to devices)</span>
        <textarea
          className="code"
          rows={9}
          value={configText}
          onChange={(e) => setConfigText(e.target.value)}
          spellCheck={false}
        />
      </label>

      <label className="check">
        <input
          type="checkbox"
          checked={isAppUpdate}
          onChange={(e) => setIsAppUpdate(e.target.checked)}
        />
        <span>
          App update (ships a signed binary artifact, not just config)
        </span>
      </label>

      {isAppUpdate && (
        <div className="artifact-fields">
          <label className="field">
            <span>Artifact object key (R2)</span>
            <input
              className="code"
              value={artifactKey}
              onChange={(e) => setArtifactKey(e.target.value)}
              placeholder="artifacts/kinsafe-2026.06.15-a.exe"
              spellCheck={false}
            />
          </label>
          <label className="field">
            <span>SHA-256 (hex)</span>
            <input
              className="code"
              value={artifactSha256}
              onChange={(e) => setArtifactSha256(e.target.value)}
              placeholder="e3b0c44298fc1c14…"
              spellCheck={false}
            />
          </label>
          <label className="field">
            <span>Ed25519 signature (base64)</span>
            <input
              className="code"
              value={artifactSig}
              onChange={(e) => setArtifactSig(e.target.value)}
              placeholder="MEUCIQ…"
              spellCheck={false}
            />
          </label>
          <p className="note">
            The device updater re-verifies SHA-256 + signature before applying;
            these refs only tell it what to fetch and check.
          </p>
        </div>
      )}

      <label className="field">
        <span>Notes</span>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="what changed / hypothesis"
        />
      </label>
      {error && <div className="signin-error">{error}</div>}
      {ok && <div className="ok-msg">Created draft release ✓</div>}
      <button className="btn btn-primary" disabled={busy}>
        {busy ? "Creating…" : "Create draft release"}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// START ROLLOUT — kicks the durable canary->fleet workflow for a tenant +
// channel + release. Auth-gated; 401 surfaced inline.
// ---------------------------------------------------------------------------
function StartRolloutForm({
  releases,
}: {
  releases:
    | { _id: Id<"releases">; version: string; channel: Channel; status: string }[]
    | undefined;
}) {
  const startRollout = useMutation(api.rollout.startRollout);
  const fleet = useQuery(api.fleet.fleetView, {});
  const [tenantId, setTenantId] = useState<string>("");
  const [channel, setChannel] = useState<Channel>("exp-A");
  const [releaseId, setReleaseId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const tenants = fleet ?? [];

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!tenantId || !releaseId) return;
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const workflowId = await startRollout({
        tenantId: tenantId as Id<"tenants">,
        channel,
        releaseId: releaseId as Id<"releases">,
      });
      setMsg(`Rollout started (workflow ${workflowId}). Open the monitor.`);
    } catch (e2) {
      setErr(authMessage(e2, "start rollout"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={submit}>
      <h2 className="panel-title">Start rollout</h2>
      <p className="note">
        Kicks the durable canary → fleet workflow: one canary device gets the
        release; only a passing selftest promotes it fleet-wide. Operator
        sign-in required.
      </p>
      <label className="field">
        <span>Tenant</span>
        <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
          <option value="">— select tenant —</option>
          {tenants.map((t) => (
            <option key={t.tenantId} value={t.tenantId}>
              {t.tenantName} ({t.kind})
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Channel</span>
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as Channel)}
        >
          {CHANNELS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Release</span>
        <select
          value={releaseId}
          onChange={(e) => setReleaseId(e.target.value)}
        >
          <option value="">— select release —</option>
          {(releases ?? []).map((r) => (
            <option key={r._id} value={r._id}>
              {r.version} · {r.channel} · {r.status}
            </option>
          ))}
        </select>
      </label>
      <button
        className="btn btn-primary"
        disabled={busy || !tenantId || !releaseId}
      >
        {busy ? "Starting…" : "Start rollout"}
      </button>
      {msg && <div className="ok-msg">{msg}</div>}
      {err && <div className="signin-error">{err}</div>}
    </form>
  );
}

// Map a thrown Convex error to a friendly operator message; detect the 401
// auth gate (startRollout / killRelease throw "401: operator authentication
// required") and surface a clear "sign in" prompt rather than a raw stack.
function authMessage(err: unknown, action: string): string {
  const s = String(err);
  if (s.includes("401") || s.toLowerCase().includes("authentication")) {
    return `Not authorized to ${action} — sign in as an operator and retry.`;
  }
  return `Couldn't ${action}: ${s}`;
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      <div className="empty-body">{body}</div>
    </div>
  );
}

function Skeleton() {
  return <div className="skeleton">Loading…</div>;
}
